import { Button } from '@latitude/button';
import { Text } from '@latitude/text';
import { useToast } from '@latitude/toast';
import { Component, type ErrorInfo, type ReactNode } from 'react';

import { reportRenderError } from './reportRenderError';

const isChunkLoadError = (error: unknown): error is Error =>
  error instanceof Error && /ChunkLoadError/.test(error.name);

type ToastError = (error: Error) => void;

type ReloadAffordanceProps = {
  onReload: () => void;
};

const ReloadAffordance = ({ onReload }: ReloadAffordanceProps) => (
  <div
    role="alert"
    className="gap-spacing-sys-spacing-20 p-spacing-sys-spacing-40 flex items-center"
    data-testid="lazy-chunk-reload"
  >
    <Text>Something failed to load. Reload the page to try again.</Text>
    <Button
      variant="primary"
      size="small"
      onClick={onReload}
      data-testid="lazy-chunk-reload-button"
    >
      Reload page
    </Button>
  </div>
);

type ClassBoundaryProps = {
  children: ReactNode;
  toastError: ToastError;
};

type ClassBoundaryState = {
  error: Error | null;
  retries: number;
};

class LazyChunkErrorBoundaryClass extends Component<
  ClassBoundaryProps,
  ClassBoundaryState
> {
  state: ClassBoundaryState = { error: null, retries: 0 };

  static getDerivedStateFromError(error: Error): Partial<ClassBoundaryState> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Defensive gate: per React's contract `componentDidCatch` runs for any
    // caught descendant error. In practice, when our render() re-throws the
    // non-chunk case, React skips commit and this method is bypassed — which
    // is why the branch below is unreachable in tests. We still gate to avoid
    // double-reporting to Sentry and surfacing a misleading "Failed to load"
    // toast if a future React version or concurrent-mode change exposes the
    // path. The outer error boundary owns non-chunk errors.
    /* istanbul ignore if -- defensive; see comment above */
    if (!isChunkLoadError(error)) {
      return;
    }
    reportRenderError(error, info);
    this.props.toastError(error);
    if (this.state.retries === 0) {
      // Silent retry: clear the error and bump retries so children remount and
      // next/dynamic re-attempts the import().
      this.setState({ error: null, retries: 1 });
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    const { error, retries } = this.state;

    if (error && !isChunkLoadError(error)) {
      throw error;
    }

    if (error && retries >= 1) {
      return <ReloadAffordance onReload={this.handleReload} />;
    }

    if (error) {
      // First catch — render nothing while we wait for the post-commit silent
      // retry triggered from componentDidCatch.
      return null;
    }

    return this.props.children;
  }
}

type LazyChunkErrorBoundaryProps = {
  children: ReactNode;
};

export const LazyChunkErrorBoundary = ({
  children,
}: LazyChunkErrorBoundaryProps) => {
  const { toast } = useToast();
  const toastError: ToastError = (error) => {
    toast({
      variant: 'danger',
      title: 'Failed to load part of the page',
      description:
        error.message || 'Retrying automatically. Reload if this persists.',
    });
  };
  return (
    <LazyChunkErrorBoundaryClass toastError={toastError}>
      {children}
    </LazyChunkErrorBoundaryClass>
  );
};
